import createClient, { type Middleware } from 'openapi-fetch';
import { getAccessToken, setAccessToken } from '@/lib/auth/token';
import type { paths } from './schema';

// Prefer runtime config (public/config.js — editable on the server without a
// rebuild), then the build-time env, then a same-origin default.
const runtimeBase =
  typeof window !== 'undefined' ? window.__CFH_CONFIG__?.API_BASE_URL : undefined;
export const API_BASE = runtimeBase || import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Listeners notified when the session is forcibly ended (refresh failed). */
const sessionEndedListeners = new Set<() => void>();
export function onSessionEnded(cb: () => void): () => void {
  sessionEndedListeners.add(cb);
  return () => sessionEndedListeners.delete(cb);
}

// ── Single-flight refresh ─────────────────────────────────────────────────────
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // send the HTTP-only refresh cookie
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) return false;
  const data = (await res.json()) as { access_token?: string };
  if (!data.access_token) return false;
  setAccessToken(data.access_token);
  return true;
}

function ensureRefresh(): Promise<boolean> {
  refreshPromise ??= refreshAccessToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

const RETRIED = Symbol('retried');

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = getAccessToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    // Never try to refresh the refresh/login endpoints, and only retry once.
    const url = new URL(request.url);
    if (url.pathname.endsWith('/auth/refresh') || url.pathname.endsWith('/auth/login')) {
      return response;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((request as any)[RETRIED]) return response;

    const ok = await ensureRefresh();
    if (!ok) {
      setAccessToken(null);
      sessionEndedListeners.forEach((cb) => cb());
      return response;
    }
    const retry = request.clone();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (retry as any)[RETRIED] = true;
    retry.headers.set('Authorization', `Bearer ${getAccessToken()}`);
    return fetch(retry);
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE,
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
});
api.use(authMiddleware);
